import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Route as RouteIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { PROGRAMME_STATUS_LABELS } from "@/config/trainingCatalog";
import { ProgrammeStatusBadge } from "@/components/child/TrainingTab";

export default function ProgrammesPage() {
  const [items, setItems] = useState(null);
  const [filters, setFilters] = useState({ status: "", council_id: "", school_id: "", trainer_id: "" });
  const [councils, setCouncils] = useState([]);
  const [schools, setSchools] = useState([]);
  const [trainers, setTrainers] = useState([]);

  const load = (f = filters) => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/programmes", { params }).then((r) => setItems(r.data.items)).catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => {
    load();
    api.get("/councils").then((r) => setCouncils(r.data.items || [])).catch(() => {});
    api.get("/schools").then((r) => setSchools(r.data.items || [])).catch(() => {});
    api.get("/staff").then((r) => setTrainers((r.data.items || []).filter((s) => s.role === "travel_trainer"))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = (key, value) => {
    const next = { ...filters, [key]: value === "all" ? "" : value };
    setFilters(next);
    load(next);
  };

  return (
    <div className="space-y-4" data-testid="programmes-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Training programmes</h1>
      </div>

      <Card>
        <CardContent className="p-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Select value={filters.status || "all"} onValueChange={(v) => apply("status", v)}>
            <SelectTrigger data-testid="programmes-filter-status" className="h-11"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(PROGRAMME_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.council_id || "all"} onValueChange={(v) => apply("council_id", v)}>
            <SelectTrigger data-testid="programmes-filter-council" className="h-11"><SelectValue placeholder="Local authority" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All authorities</SelectItem>
              {councils.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.school_id || "all"} onValueChange={(v) => apply("school_id", v)}>
            <SelectTrigger data-testid="programmes-filter-school" className="h-11"><SelectValue placeholder="School" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All schools</SelectItem>
              {schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.trainer_id || "all"} onValueChange={(v) => apply("trainer_id", v)}>
            <SelectTrigger data-testid="programmes-filter-trainer" className="h-11"><SelectValue placeholder="Trainer" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All trainers</SelectItem>
              {trainers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><RouteIcon className="h-5 w-5 text-primary" /> Programmes ({items?.length ?? "…"})</CardTitle></CardHeader>
        <CardContent>
          {items === null ? (
            <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-600" data-testid="programmes-empty">No programmes match the selected filters.</p>
          ) : (
            <div className="space-y-2">
              {items.map((p) => (
                <Link key={p.id} to={`/programmes/${p.id}`} data-testid={`programme-row-${p.programme_number}`}
                  className="flex flex-wrap items-center gap-3 rounded-md border border-border p-3 hover:bg-muted transition-colors duration-200">
                  <div className="min-w-[160px]">
                    <p className="font-mono font-semibold text-slate-900">{p.programme_number}</p>
                    <p className="text-xs text-slate-500">{p.child?.child_name} · {p.child?.case_id}</p>
                  </div>
                  <div className="text-xs text-slate-600 min-w-[140px]">
                    <p>Trainer: {p.trainer_name || "—"}</p>
                    <p>{p.current_week ? `Week ${p.current_week}/${p.planned_weeks}` : `${p.planned_weeks} weeks planned`} · {p.sessions_completed}/{p.sessions_total} done</p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {p.behind_schedule && <Badge className="bg-red-50 text-red-700 border-transparent">Behind schedule</Badge>}
                    {p.interventions > 0 && <Badge className="bg-amber-100 text-amber-800 border-transparent">{p.interventions} intervention{p.interventions > 1 ? "s" : ""}</Badge>}
                    <ProgrammeStatusBadge status={p.status} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
